import React from 'react';
import { Box, HStack, VStack, IconButton, Tooltip, Divider } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import Logo from './Logo';
import SidebarTreeItem from './SidebarTreeItem';
import { useSidebarNavigation, SIDEBAR_WIDTH, COLLAPSED_WIDTH } from '../../hooks/useSidebarNavigation';

export default function SidebarContent({
  collapsed,
  onToggle,
  onNavigate,
  role,
  isPinned,
}: {
  collapsed: boolean;
  onToggle: () => void;
  onNavigate: (path: string) => void;
  role: 'ADMIN' | 'TEACHER' | 'STUDENT' | 'UNKNOWN';
  isPinned?: boolean;
}): JSX.Element {
  const { t, filteredNav, openMap, toggle, normalizedQuery } = useSidebarNavigation(role);

  return (
    <Box
      position="sticky"
      top={0}
      h="100vh"
      w={{ base: 'full', md: collapsed ? `${COLLAPSED_WIDTH}px` : `${SIDEBAR_WIDTH}px` }}
      minW={{ base: 'full', md: collapsed ? `${COLLAPSED_WIDTH}px` : `${SIDEBAR_WIDTH}px` }}
      maxW={{ base: 'full', md: collapsed ? `${COLLAPSED_WIDTH}px` : `${SIDEBAR_WIDTH}px` }}
      borderRightWidth="1px"
      p={collapsed ? 2 : 4}
      bg="white"
      display="flex"
      flexDirection="column"
      boxShadow={isPinned ? 'none' : 'lg'}
      zIndex={isPinned ? 1 : 1001}
    >
      <HStack justify="space-between">
        <Box>
          {collapsed ? (
            <IconButton
              aria-label={t('common.expand')}
              size="sm"
              onClick={onToggle}
              icon={<ChevronRightIcon />}
            />
          ) : (
            <Logo collapsed={collapsed} />
          )}
        </Box>
        {!collapsed && (
          <Tooltip
            label={isPinned ? t('common.unpinSidebar') : t('common.pinSidebar')}
            placement="right"
          >
            <IconButton
              aria-label={isPinned ? t('common.unpinSidebar') : t('common.pinSidebar')}
              size="sm"
              onClick={onToggle}
              icon={isPinned ? <ChevronLeftIcon /> : <ChevronRightIcon />}
              colorScheme={isPinned ? 'blue' : 'gray'}
              variant={isPinned ? 'solid' : 'ghost'}
            />
          </Tooltip>
        )}
      </HStack>
      <Divider my={4} />

      <Box overflowY="auto" pr={1} flex="1">
        <VStack align="stretch" spacing={2}>
          {filteredNav.map((node) => (
            <Box key={node.id}>
              <SidebarTreeItem
                node={node}
                collapsed={collapsed}
                onNavigate={onNavigate}
                openMap={openMap}
                toggle={toggle}
                normalizedQuery={normalizedQuery}
                t={t}
              />
            </Box>
          ))}
        </VStack>
      </Box>
    </Box>
  );
}
